import apiService from 'library/api-service';
import { Message, ChatSendPayload } from './chat.types';
import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { initializeChat, addMessage, setSessionId, resetChatState } from 'store/features/chat/slice';

const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const useChat = () => {
  const dispatch = useAppDispatch();
  const { sessionId, messages } = useAppSelector((state) => state.chat);
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize session ID and greeting message on load
  useEffect(() => {
    dispatch(initializeChat());
  }, [dispatch]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !sessionId) return;

      const userMsg: Message = {
        id: generateUUID(),
        sender: 'user',
        text: text.trim(),
        timestamp: new Date(),
      };

      dispatch(addMessage(userMsg));
      setLoading(true);

      try {
        const payload: ChatSendPayload = {
          sessionId,
          message: text.trim(),
        };

        const response = await apiService.sendChatMessage(payload);

        let botResponseText = '';
        if (response.isSuccess && response.data) {
          const rawResponse = response.data.response;
          botResponseText = rawResponse;

          // Update sessionId if the backend returned a different one
          if (response.data.sessionId && response.data.sessionId !== sessionId) {
            dispatch(setSessionId(response.data.sessionId));
          }

          // Try to parse if the text itself is a JSON string
          try {
            const parsed = JSON.parse(rawResponse);
            if (typeof parsed === 'object' && parsed !== null) {
              botResponseText = parsed.text || parsed.message || parsed.response || JSON.stringify(parsed);
            }
          } catch {
            // It's a plain string, keep it as is
          }
        } else {
          botResponseText = response.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
        }

        const botMsg: Message = {
          id: generateUUID(),
          sender: 'bot',
          text: botResponseText,
          timestamp: new Date(),
        };

        dispatch(addMessage(botMsg));
      } catch (error) {
        const botMsg: Message = {
          id: generateUUID(),
          sender: 'bot',
          text: 'Üzgünüm, şu anda yanıt alamıyorum. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.',
          timestamp: new Date(),
        };
        dispatch(addMessage(botMsg));
      } finally {
        setLoading(false);
      }
    },
    [sessionId, dispatch]
  );

  const resetChat = useCallback(() => {
    dispatch(resetChatState());
  }, [dispatch]);

  return {
    messages,
    loading,
    sendMessage,
    resetChat,
  };
};
export default useChat;
